import { useServerLog } from '@/api'
import { useAppSelector } from '@/app'
import { onlineManager } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { selectors } from './coreSlice'
import {
  applyOnlineRecovery,
  recoveryTick,
  shouldArmWatchdogProbe,
  shouldProbeForOnline,
  shouldProbeOnOfflineTransition,
} from './onlineRecovery'

/** Abort a single probe if no response within this long (treat as offline). */
const PROBE_TIMEOUT_MS = 5_000
/** Gap between probe attempts while a recovery session is running. */
const RECOVERY_RETRY_INTERVAL_MS = 3_000
/**
 * Wall-clock budget for one recovery session, after which we give up until the
 * next lifecycle event re-arms. A wall-clock bound (not an attempt count) keeps
 * the duration honest regardless of how long each probe takes when offline
 * (up to PROBE_TIMEOUT_MS each); it caps request cost when truly offline.
 */
const RECOVERY_MAX_DURATION_MS = 60_000
/**
 * Cadence of the watchdog that re-arms recovery while the page is visible and
 * react-query is stuck offline (see `shouldArmWatchdogProbe`). This is what
 * heals the persistent stuck-offline state the lifecycle-event arms miss: once a
 * 60s session exhausts, or when the page resumed already-offline with no fresh
 * event, the watchdog keeps trying. It is a no-op (no probe) while online or
 * hidden, so it costs nothing on a healthy session.
 */
const WATCHDOG_INTERVAL_MS = 20_000

const DOCUMENT_EVENTS = ['visibilitychange', 'resume'] as const
const WINDOW_EVENTS = ['pageshow', 'online'] as const

const currentVisibility = () =>
  typeof document === 'undefined' ? 'unknown' : document.visibilityState

const currentNavigatorOnLine = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine

/**
 * Recovers the live-update loop after a freeze/resume that left react-query
 * stuck offline (see `onlineRecovery.ts` for the why).
 *
 * The live-update polling pauses while `onlineManager` reports offline. Across a
 * background freeze the browser can drop the `online` event (and `navigator.onLine`
 * itself lags or stays wrongly false on these devices), so `onlineManager` gets
 * stuck offline and every query stays `paused` until a manual reload.
 *
 * Two-tier recovery on a resume/visibility/online event, an offline transition,
 * or a watchdog tick (see `recoveryActionFor`):
 *  - FAST PATH — react-query offline but `navigator.onLine` true: this is every
 *    confirmed prod `recoveryFailed` signature, so trust the browser and call
 *    `onlineManager.setOnline(true)` immediately, unpausing polling now so the
 *    visit self-heal refetch is not born paused (no probe round to wait out).
 *  - SLOW PATH — `navigator.onLine` also false (the wrongly-false case the probe
 *    was built for): arm a session that probes the backend with a lightweight
 *    `no-cors` HEAD every few seconds while visible and offline. The first
 *    reachable response forces online and ends the session; a genuine network
 *    failure just retries (no force-online) until the ~1 min wall-clock deadline,
 *    after which the next lifecycle event / watchdog tick re-arms.
 *
 * The probe reaches the server even when `navigator.onLine` is wrongly false, so
 * a lagging flag, a dropped `online` event, or a brief offline-at-resume all
 * self-heal on a later tick. Deliberately separate from the observation-only
 * `useUpdateHealthMonitor`, which must take no corrective action. Mount once in
 * the authenticated shell.
 */
export const useOnlineRecovery = () => {
  const baseUrl = useAppSelector(selectors.selectBackend).baseUrl
  const serverLog = useServerLog()

  const baseUrlRef = useRef(baseUrl)
  baseUrlRef.current = baseUrl
  const serverLogRef = useRef(serverLog)
  serverLogRef.current = serverLog

  // Recovery-session state.
  const activeRef = useRef(false)
  const sessionStartRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    /** One reachability probe. Resolves true if the server responded at all. */
    const probeReachable = async (): Promise<boolean> => {
      const url = baseUrlRef.current
      if (!url) {
        return false
      }
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
      try {
        // `no-cors` HEAD resolves (opaque) for any reachable server and rejects
        // only on a genuine network failure — the connectivity signal we want,
        // independent of the unreliable `navigator.onLine`, with no CORS/auth
        // complications.
        await fetch(url, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal,
        })
        return true
      } catch {
        return false
      } finally {
        clearTimeout(timeout)
      }
    }

    const stopRecovery = () => {
      activeRef.current = false
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const attemptRecovery = async () => {
      const action = recoveryTick(
        onlineManager.isOnline(),
        currentVisibility() === 'visible',
        Date.now() - sessionStartRef.current,
        RECOVERY_MAX_DURATION_MS
      )
      if (action !== 'probe') {
        stopRecovery()
        return
      }
      const reachable = await probeReachable()
      if (!activeRef.current) {
        // Session was torn down (unmount) while the probe was in flight.
        return
      }
      if (reachable && !onlineManager.isOnline()) {
        onlineManager.setOnline(true)
        try {
          serverLogRef.current.warn(
            '[online-recovery] connectivity confirmed after resume; resumed react-query (was offline)'
          )
        } catch {
          // Never let diagnostics logging throw into the app.
        }
        stopRecovery()
        return
      }
      if (onlineManager.isOnline()) {
        stopRecovery()
        return
      }
      timerRef.current = setTimeout(() => {
        void attemptRecovery()
      }, RECOVERY_RETRY_INTERVAL_MS)
    }

    /**
     * Start a recovery session. No-op if one is already running, react-query is
     * already online, or there is no backend URL to probe yet (otherwise the
     * session would burn its whole budget on probes guaranteed to fail).
     */
    const armRecovery = () => {
      if (
        activeRef.current ||
        onlineManager.isOnline() ||
        !baseUrlRef.current
      ) {
        return
      }
      activeRef.current = true
      sessionStartRef.current = Date.now()
      void attemptRecovery()
    }

    // Fast path for the dominant stuck-offline signature: when react-query is
    // wrongly offline but `navigator.onLine` is true (every confirmed prod
    // `recoveryFailed` case), `applyOnlineRecovery` flips onlineManager online
    // immediately so the visit self-heal refetch is not born paused. Only the
    // navigator-wrongly-false case falls back to the async probe.
    const recoverOnline = () =>
      applyOnlineRecovery(onlineManager, currentNavigatorOnLine(), {
        onForce: () => {
          try {
            serverLogRef.current.warn(
              '[online-recovery] forced online from navigator.onLine (react-query was stuck offline after resume)'
            )
          } catch {
            // Never let diagnostics logging throw into the app.
          }
        },
        onProbe: armRecovery,
      })

    const handler = (event: Event) => {
      if (
        shouldProbeForOnline(
          event.type,
          currentVisibility(),
          onlineManager.isOnline()
        )
      ) {
        recoverOnline()
      }
    }

    DOCUMENT_EVENTS.forEach(type =>
      document.addEventListener(type, handler, { passive: true })
    )
    WINDOW_EVENTS.forEach(type =>
      window.addEventListener(type, handler, { passive: true })
    )

    // Also arm on react-query transitioning to offline while visible — catches
    // the post-resume case where the offline flip lags the lifecycle events.
    const unsubscribe = onlineManager.subscribe((online: boolean) => {
      if (shouldProbeOnOfflineTransition(online, currentVisibility())) {
        recoverOnline()
      }
    })

    // Watchdog: the event-driven arms above each fire once, so a stuck-offline
    // state that outlives a recovery session — or that resumed already-offline
    // with no fresh event — would never re-probe while the user stares at a
    // frozen calendar. This re-arms on a steady cadence whenever visible and
    // offline; `armRecovery` no-ops if a session is already running or we are
    // back online, so it is free on a healthy session.
    const watchdogId = setInterval(() => {
      if (
        shouldArmWatchdogProbe(currentVisibility(), onlineManager.isOnline())
      ) {
        recoverOnline()
      }
    }, WATCHDOG_INTERVAL_MS)

    return () => {
      DOCUMENT_EVENTS.forEach(type =>
        document.removeEventListener(type, handler)
      )
      WINDOW_EVENTS.forEach(type => window.removeEventListener(type, handler))
      unsubscribe()
      clearInterval(watchdogId)
      stopRecovery()
    }
    // Listeners close over stable refs only.
  }, [])
}
